import { Link as RouterLink } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import type { AssistantBriefing } from './nudge'
import { assistantCasePath } from './results/AssistantCaseCard'
import SparkleIcon from './SparkleIcon'

interface AssistantBriefingCardProps {
  briefing: AssistantBriefing
  /** Sends a follow-up as a normal message (a model turn). */
  onFollowUp: (question: string, caseId?: string) => void
  disabled?: boolean
}

const HEADING_ID = 'assistant-briefing-heading'

/** "expires in 5 hours" -> "Expires in 5 hours" */
const capitalise = (text: string) =>
  text.charAt(0).toUpperCase() + text.slice(1)

/**
 * The nudge's task as a compact card, in the style of the answer cards. Built
 * from My Tasks data on screen only: never a chat message, so the model
 * never sees it. Its follow-ups ask the model like any other question.
 */
const AssistantBriefingCard = ({
  briefing,
  onFollowUp,
  disabled,
}: AssistantBriefingCardProps) => {
  const title = briefing.title || 'Your most urgent task'
  return (
    <Box
      role="region"
      aria-labelledby={HEADING_ID}
      data-testid="assistant-briefing"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 0.5,
        p: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <SparkleIcon small color="primary" sx={{ fontSize: 16 }} />
        <Typography variant="caption" fontWeight={700} color="primary">
          From your tasks
        </Typography>
      </Box>
      <Typography
        id={HEADING_ID}
        variant="body2"
        component="h3"
        fontWeight={700}
        sx={{ overflowWrap: 'anywhere' }}
      >
        {title}
      </Typography>
      <Typography variant="body2" data-testid="assistant-briefing-action">
        {briefing.actionLabel}
        {briefing.due && (
          <Box
            component="span"
            sx={{ color: briefing.dueUrgent ? 'error.main' : 'text.secondary' }}
          >
            {` · ${capitalise(briefing.due)}`}
          </Box>
        )}
      </Typography>
      {briefing.facts.length > 0 && (
        <Typography variant="caption" color="text.secondary" component="p">
          {briefing.facts.join(' · ')}
        </Typography>
      )}
      <Button
        component={RouterLink}
        to={assistantCasePath(briefing.caseId)}
        variant="outlined"
        size="small"
        data-testid="assistant-briefing-case-link"
        aria-label={`Open Case: ${title}`}
        sx={{ mt: 0.75, py: 0.25, px: 1.25, minWidth: 0 }}
      >
        Open Case
      </Button>
      {briefing.followUps.length > 0 && (
        <Box
          aria-label="Follow-up questions"
          role="group"
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}
        >
          {briefing.followUps.map(followUp => (
            <Chip
              key={followUp.label}
              label={followUp.label}
              color="primary"
              variant="outlined"
              disabled={disabled}
              onClick={() => onFollowUp(followUp.question, followUp.caseId)}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default AssistantBriefingCard
